import { Field, FieldInputProps, Form, Formik, FormikProps } from "formik";
import * as Yup from "yup";
import {
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  Input,
  Text,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import AppButton from "@/features/line-sheet-sets/app-button";
import AppMyAccountCard from "@/features/my-account/app-my-account-card";
import BusinessRegistrationCertificateRow, {
  BusinessRegistrationCertificateStatus,
} from "@/shared/components/business-registration-certificate-row";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useParseBusinessRegistrationCertificate from "@/shared/hooks/business-registration-certificate/useParseBusinessRegistrationCertificate";
import {
  useGetBoutiqueBusinessInfo,
  useUpdateBoutiqueBusinessInfo,
} from "@/features/my-account/hooks/use-boutique-account";
import {
  BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT,
  BUSINESS_REGISTRATION_NUMBER_PATTERN,
  formatBusinessRegistrationNumber,
  isAcceptableBusinessRegistrationCertificateType,
  MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE,
} from "@/shared/constants/business-registration-certificate";

interface FormValues {
  businessRegistrationNumber: string;
  businessName: string;
}

// 자동 인식까지 끝났지만 아직 저장하지 않은 파일 — 저장 전에는 서버 URL이 없어 원본 File로 보여준다.
interface PendingCertificate {
  unoFileId: number;
  file: File;
}

const validationSchema = Yup.object({
  businessRegistrationNumber: Yup.string().matches(
    BUSINESS_REGISTRATION_NUMBER_PATTERN,
    {
      message: "사업자등록번호 형식이 올바르지 않습니다. (예: 123-45-67890)",
      excludeEmptyString: true,
    }
  ),
  businessName: Yup.string(),
});

// 부티크 사업자 정보 — 바이어 카드와 달리 사업자등록증 없이도 번호·사업자명을 저장할 수 있다.
// 여기 저장한 사업자명·사업자등록번호가 오더 거래명세서의 FROM 에 찍힌다.
export default function BoutiqueBusinessInfoCard() {
  const { info } = useAppToasts({ id: "GLOBAL" });
  const { data: businessInfo, isPending: isLoading } =
    useGetBoutiqueBusinessInfo();
  const { parseBusinessRegistrationCertificate, isPending: isParsing } =
    useParseBusinessRegistrationCertificate();
  const { mutateAsync: updateBusinessInfo, isPending: isSaving } =
    useUpdateBoutiqueBusinessInfo();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlsRef = useRef<string[]>([]);
  const formikRef = useRef<FormikProps<FormValues> | null>(null);
  const [pendingCertificate, setPendingCertificate] =
    useState<PendingCertificate | null>(null);
  const [parsingFileName, setParsingFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const certificate = businessInfo?.businessRegistrationCertificate ?? null;

  useEffect(() => {
    const objectUrls = objectUrlsRef.current;
    return () => objectUrls.forEach((url) => URL.revokeObjectURL(url));
  }, []);

  useEffect(() => {
    // 사용자가 고치고 있는 값은 덮어쓰지 않는다.
    if (!businessInfo || !formikRef.current || formikRef.current.dirty) {
      return;
    }
    formikRef.current.resetForm({
      values: {
        businessRegistrationNumber: formatBusinessRegistrationNumber(
          businessInfo.businessRegistrationNumber ?? ""
        ),
        businessName: businessInfo.businessName ?? "",
      },
    });
  }, [businessInfo]);

  const status: BusinessRegistrationCertificateStatus = isParsing
    ? "PARSING"
    : pendingCertificate
    ? "UNSAVED"
    : certificate
    ? "SAVED"
    : isLoading
    ? "LOADING"
    : "EMPTY";

  const registeredAt = useMemo(
    () =>
      certificate?.registeredAt ? new Date(certificate.registeredAt) : null,
    [certificate]
  );

  const handleViewFile = () => {
    if (pendingCertificate) {
      const url = URL.createObjectURL(pendingCertificate.file);
      objectUrlsRef.current.push(url);
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    if (certificate) {
      window.open(certificate.url, "_blank", "noopener,noreferrer");
    }
  };

  // 파일을 고르면 자동 인식해 사업자등록번호·사업자명을 채운다(비어 있던 칸만이 아니라 덮어쓴다).
  const handleFileSelect = async (
    file: File,
    form: FormikProps<FormValues>
  ) => {
    setFileError(null);
    if (!isAcceptableBusinessRegistrationCertificateType(file)) {
      setFileError("PDF, JPG, JPEG, PNG 파일만 첨부할 수 있습니다.");
      return;
    }
    if (file.size > MAX_BUSINESS_REGISTRATION_CERTIFICATE_FILE_SIZE) {
      setFileError("파일 크기는 최대 10MB까지 첨부할 수 있습니다.");
      return;
    }
    setPendingCertificate(null);
    setParsingFileName(file.name);
    try {
      const { unoFileId, result } = await parseBusinessRegistrationCertificate(
        file
      );
      setPendingCertificate({ unoFileId, file });
      form.setValues((prev) => ({
        businessRegistrationNumber: result.businessRegistrationNumber
          ? formatBusinessRegistrationNumber(result.businessRegistrationNumber)
          : prev.businessRegistrationNumber,
        businessName: result.companyName ?? prev.businessName,
      }));
    } catch {
      setFileError(
        "사업자등록증을 읽지 못했습니다. 다른 파일을 첨부해 주세요."
      );
    } finally {
      setParsingFileName(null);
    }
  };

  return (
    <AppMyAccountCard title={"사업자 정보"}>
      <Formik<FormValues>
        initialValues={{ businessRegistrationNumber: "", businessName: "" }}
        validationSchema={validationSchema}
        innerRef={formikRef}
        onSubmit={async (values, actions) => {
          try {
            await updateBusinessInfo({
              businessRegistrationCertificateFileId:
                pendingCertificate?.unoFileId ?? null,
              // 빈 칸으로 저장하면 지운다.
              businessRegistrationNumber: values.businessRegistrationNumber,
              businessName: values.businessName,
            });
            setPendingCertificate(null);
            info("사업자 정보가 저장되었습니다.");
            actions.resetForm({ values });
          } catch {
            // 실패 토스트는 저장 훅의 onError가 띄운다.
          }
        }}
      >
        {(props) => (
          <Form>
            <input
              type={"file"}
              style={{ display: "none" }}
              ref={fileInputRef}
              accept={BUSINESS_REGISTRATION_CERTIFICATE_ACCEPT}
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                if (file) {
                  handleFileSelect(file, props);
                }
              }}
            />
            <Flex flexDirection={"column"} gap={"16px"}>
              <Flex flexDirection={"column"} gap={"12px"}>
                <BusinessRegistrationCertificateRow
                  status={status}
                  fileName={
                    parsingFileName ??
                    pendingCertificate?.file.name ??
                    certificate?.name ??
                    null
                  }
                  registeredAt={registeredAt}
                  onViewFile={handleViewFile}
                />
                <Flex
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  gap={"8px"}
                  flexWrap={"wrap"}
                >
                  <Text
                    fontSize={"12px"}
                    color={fileError ? "#CC1D1D" : "#8F8F8C"}
                  >
                    {fileError ?? "PDF, JPG, JPEG, PNG · 최대 10MB"}
                  </Text>
                  <AppButton
                    variant={"primary"}
                    size={"small"}
                    isDisabled={isParsing || isSaving}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {certificate || pendingCertificate
                      ? "사업자등록증 변경"
                      : "사업자등록증 등록"}
                  </AppButton>
                </Flex>
              </Flex>

              <Flex
                flexDirection={"column"}
                gap={"8px"}
                padding={"16px"}
                borderRadius={"4px"}
                border={"0.5px solid #EFEFED"}
                background={"#F7F8FA"}
              >
                <Text fontSize={"12px"} color={"#6F6F6F"}>
                  사업자명과 사업자등록번호는 거래명세서의 공급자(FROM)에
                  표시됩니다. 사업자등록증을 등록하면 자동으로 채워집니다.
                </Text>
                <Grid
                  gridTemplateColumns={{ base: "1fr", md: "1fr 1fr" }}
                  columnGap={"16px"}
                  rowGap={"12px"}
                  paddingTop={"8px"}
                >
                  <Field name={"businessRegistrationNumber"}>
                    {({
                      field,
                      form,
                    }: {
                      field: FieldInputProps<string>;
                      form: FormikProps<FormValues>;
                    }) => (
                      <FormControl
                        isInvalid={
                          !!form.errors.businessRegistrationNumber &&
                          !!form.touched.businessRegistrationNumber
                        }
                      >
                        <FormLabel display={"flex"} m={"0"}>
                          <Text
                            color={"#6F6F6F"}
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            사업자등록번호
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          fontSize={"12px"}
                          minHeight={"0"}
                          height={"28px"}
                          background={"#FFF"}
                          inputMode={"numeric"}
                          maxLength={12}
                          placeholder={"123-45-67890"}
                          onChange={(event) => {
                            form.setFieldValue(
                              field.name,
                              formatBusinessRegistrationNumber(
                                event.target.value
                              )
                            );
                          }}
                        />
                        <Box minHeight={"16px"}>
                          <FormErrorMessage
                            m={"0"}
                            mt={"4px"}
                            fontSize={"12px"}
                          >
                            {form.errors.businessRegistrationNumber}
                          </FormErrorMessage>
                        </Box>
                      </FormControl>
                    )}
                  </Field>
                  <Field name={"businessName"}>
                    {({ field }: { field: FieldInputProps<string> }) => (
                      <FormControl>
                        <FormLabel display={"flex"} m={"0"}>
                          <Text
                            color={"#6F6F6F"}
                            fontSize={"12px"}
                            fontWeight={"400"}
                          >
                            사업자명
                          </Text>
                        </FormLabel>
                        <Input
                          {...field}
                          fontSize={"12px"}
                          minHeight={"0"}
                          height={"28px"}
                          background={"#FFF"}
                          placeholder={"사업자명을 입력해 주세요"}
                        />
                        <Box minHeight={"16px"} />
                      </FormControl>
                    )}
                  </Field>
                </Grid>
              </Flex>

              <Flex justifyContent={"flex-end"}>
                <AppButton
                  variant={"primary"}
                  size={"medium"}
                  type={"submit"}
                  isLoading={isSaving}
                  isDisabled={
                    (!pendingCertificate && !props.dirty) ||
                    !props.isValid ||
                    isParsing
                  }
                >
                  사업자 정보 저장
                </AppButton>
              </Flex>
            </Flex>
          </Form>
        )}
      </Formik>
    </AppMyAccountCard>
  );
}
